import { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, Skeleton, Alert } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import DocumentMetadata from './DocumentMetadata';
import { usePdfThumbnails, PdfPageDialog } from '../../PdfThumbnailRenderer';

function DocumentListItem({ doc, index, totalDocs, onEdit, onDownload }) {
  const metadata = doc.classificationData?.document?.metadata;
  const impressionId = metadata?.impression_id;
  const [thumbnail, setThumbnail] = useState(null);
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(false);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);

  const { generateThumbnail, fileUrl, numPages, pdfDoc } = usePdfThumbnails(doc.file);

  useEffect(() => {
    if (doc.file && !thumbnail && !isLoadingThumbnail) {
      setIsLoadingThumbnail(true);
      generateThumbnail(1).then((thumb) => {
        if (thumb) {
          setThumbnail(thumb);
        }
        setIsLoadingThumbnail(false);
      });
    }
  }, [doc.file, thumbnail, isLoadingThumbnail, generateThumbnail]);

  const getTmmUrl = () => {
    if (!impressionId) return null;
    return `http://192.168.135.68:8281/tmm/tenants/prod/#/productDetail/PRD/transcript%20Standard/${impressionId}/D/952/`;
  };

  const renderActions = () => {
    const buttonSx = { ml: 2, minWidth: 160 };

    switch (doc.status) {
      case 'uploading':
      case 'classifying':
        return (
          <Button
            variant="outlined"
            disabled
            startIcon={<CircularProgress size={20} />}
            sx={buttonSx}
          >
            Uploading...
          </Button>
        );

      case 'processing':
        return (
          <Button
            variant="outlined"
            disabled
            startIcon={<CircularProgress size={20} color="success" />}
            sx={buttonSx}
          >
            Processing...
          </Button>
        );

      case 'ready':
        return (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(doc)}
            sx={buttonSx}
          >
            Edit Chunks
          </Button>
        );

      case 'completed':
        return (
          <Button
            variant="contained"
            color="success"
            startIcon={<CloudDownloadIcon />}
            onClick={() => onDownload(doc)}
            sx={buttonSx}
          >
            Download
          </Button>
        );

      case 'error':
        return (
          <Button
            variant="outlined"
            color="error"
            disabled
            sx={buttonSx}
          >
            Failed
          </Button>
        );

      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (doc.status) {
      case 'completed':
        return 'rgba(76,175,80,0.05)';
      case 'error':
        return 'rgba(244,67,54,0.05)';
      default:
        return 'white';
    }
  };

  const tmmUrl = getTmmUrl();

  const handleThumbnailClick = () => {
    if (doc.file) {
      setIsPdfDialogOpen(true);
    }
  };

  const handleClosePdfDialog = () => {
    setIsPdfDialogOpen(false);
  };

  const renderValidationWarnings = () => {
    const warnings = doc.classificationData?.validation_warnings;
    if (!warnings || warnings.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {warnings.map((warning, idx) => {
          const isWarning = warning.severity === 'warning';
          const icon = isWarning ? <WarningAmberIcon fontSize="small" /> : <InfoIcon fontSize="small" />;
          const severity = isWarning ? 'warning' : 'info';

          return (
            <Alert
              key={`${warning.code}-${idx}`}
              severity={severity}
              icon={icon}
              sx={{
                py: 0.5,
                mb: idx < warnings.length - 1 ? 1 : 0,
                '& .MuiAlert-message': {
                  fontSize: '0.875rem',
                  py: 0.5
                }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {warning.code}
              </Typography>
              <Typography variant="body2">
                {warning.message}
              </Typography>
            </Alert>
          );
        })}
      </Box>
    );
  };

  const renderThumbnail = () => {
    if (!doc.file) {
      return (
        <Box
          sx={{
            width: 60,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            flexShrink: 0,
          }}
        >
          <InsertDriveFileIcon sx={{ fontSize: 32, color: '#999' }} />
        </Box>
      );
    }

    if (isLoadingThumbnail || !thumbnail) {
      return (
        <Skeleton
          variant="rectangular"
          width={60}
          height={80}
          sx={{ borderRadius: 1, flexShrink: 0 }}
        />
      );
    }

    return (
      <Box
        component="img"
        src={thumbnail}
        alt={`${doc.filename} cover`}
        onClick={handleThumbnailClick}
        sx={{
          width: 60,
          height: 80,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid #ddd',
          flexShrink: 0,
          cursor: 'pointer',
        }}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: index !== totalDocs - 1 ? '1px solid #eee' : 'none',
          backgroundColor: getBackgroundColor(),
          gap: 2,
        }}
      >
        {renderThumbnail()}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DocumentMetadata doc={doc} />
          {renderValidationWarnings()}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {tmmUrl && (
            <Button
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={tmmUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in TLM
            </Button>
          )}
          {renderActions()}
        </Box>
      </Box>

      {doc.file && (
        <PdfPageDialog
          open={isPdfDialogOpen}
          onClose={handleClosePdfDialog}
          fileUrl={fileUrl}
          pdfDoc={pdfDoc}
          fileName={doc.filename}
          totalPages={numPages}
        />
      )}
    </>
  );
}

export default DocumentListItem;
